import {Check, ArrowRight} from 'lucide-react'
import { Link } from 'react-router-dom'
import PageHero from '@/components/PageHero'
import PageCTA from '@/components/PageCTA'
import SEO from '@/components/SEO'
import { adultes, images, links } from '@/content/site'

function Adultes() {
  return (
    <div>
      <SEO
        title="Crée le canevas de ta vie — Parcours de transformation par l'art | HeartCanvas"
        description="Un parcours en 10 séances pour explorer tes liens familiaux, libérer ce qui te pèse et retrouver confiance, stabilité et sécurité intérieure."
        path="/adultes"
      />
      <PageHero
        eyebrow={adultes.hero.eyebrow}
        title={adultes.hero.title}
        noBorder
      />
      {adultes.hero.subtitle && (
        <div className="px-6 md:px-10 -mt-10 md:-mt-14 pb-6">
          <div className="max-w-6xl mx-auto">
            <p className="font-serif-display italic text-[18px] md:text-[20px] text-[#3A3236] leading-[1.55] max-w-2xl">
              {adultes.hero.subtitle}
            </p>
          </div>
        </div>
      )}

      {/* Cover image */}
      <section className="px-6 md:px-10 mt-2">
        <div className="max-w-7xl mx-auto">
          <img
            src={images.canevasOffer}
            alt="Mains tenant un pinceau pendant une séance HeartCanvas — Crée le canevas de ta vie"
            loading="lazy"
            className="w-full aspect-[16/8] object-cover"
          />
        </div>
      </section>

      {/* POUR QUI */}
      <section className="px-6 md:px-10 py-24 md:py-32">
        <div className="max-w-7xl mx-auto grid md:grid-cols-12 gap-12 md:gap-20">
          <div className="md:col-span-4">
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#C0923F] mb-6">{adultes.pourQui.eyebrow}</div>
            <h2 className="font-serif-display text-[28px] md:text-[44px] leading-[1.1] text-[#1F1B1D]">
              {adultes.pourQui.title}
            </h2>
          </div>
          <div className="md:col-span-8 md:pt-4">
            <ul className="space-y-6">
              {adultes.pourQui.items.map((line, i) => (
                <li key={i} className="flex gap-5 items-start">
                  <span className="mt-3 w-6 h-px bg-[#9E3A5A] shrink-0" />
                  <p className="font-serif-display text-xl md:text-2xl leading-[1.45] text-[#1F1B1D]">{line}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* PAS POUR TOI */}
      <section className="px-6 md:px-10 py-16 md:py-24 border-t border-[#DCC9D2]">
        <div className="max-w-7xl mx-auto grid md:grid-cols-12 gap-12 md:gap-20">
          <div className="md:col-span-4">
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#C0923F] mb-6">{adultes.pasPourtoi.eyebrow}</div>
            <h2 className="font-serif-display text-[28px] md:text-[44px] leading-[1.1] text-[#1F1B1D]">
              {adultes.pasPourtoi.title}
            </h2>
          </div>
          <div className="md:col-span-8 md:pt-4">
            <ul className="space-y-5">
              {adultes.pasPourtoi.items.map((line, i) => (
                <li key={i} className="flex gap-5 items-start">
                  <span className="mt-3 w-6 h-px bg-[#3A3236]/40 shrink-0" />
                  <p className="text-[16px] md:text-[17px] leading-[1.7] text-[#3A3236]">{line}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* PARCOURS EN 4 TEMPS */}
      <section className="bg-[#EFE5EC] border-y border-[#DCC9D2] px-6 md:px-10 py-24 md:py-32">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-12 gap-8 mb-16 md:mb-20">
            <div className="md:col-span-4">
              <div className="text-[11px] tracking-[0.3em] uppercase text-[#C0923F] mb-6">{adultes.parcours.eyebrow}</div>
            </div>
            <div className="md:col-span-8">
              <h2 className="font-serif-display text-[28px] md:text-[44px] leading-[1.1] text-[#1F1B1D] whitespace-pre-line">
                {adultes.parcours.title}
              </h2>
            </div>
          </div>

          <ol className="relative space-y-12 md:space-y-16">
            {adultes.parcours.steps.map((step, i) => (
              <li key={step.num}>
                <div className="grid md:grid-cols-12 gap-8 md:gap-12">
                  <div className="md:col-span-3">
                    <div className="font-serif-display italic text-6xl md:text-7xl text-[#9E3A5A] leading-none">{step.num}</div>
                  </div>
                  <div className="md:col-span-9">
                    <h3 className="font-serif-display text-[26px] md:text-[36px] text-[#1F1B1D] mb-5 leading-tight">
                      {i + 1}. {step.title}
                    </h3>
                    <p className="text-[17px] md:text-[18px] leading-[1.75] text-[#3A3236] max-w-2xl">{step.body}</p>
                    {step.transition && (
                      <div className="mt-6 md:mt-8 pl-6 border-l-2 border-[#C0923F] max-w-2xl">
                        <p className="font-serif-display italic text-lg md:text-xl text-[#3A3236] leading-[1.55]">
                          {step.transition}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                {i < adultes.parcours.steps.length - 1 && (
                  <div className="mt-12 md:mt-16 ml-0 md:ml-[25%] h-px bg-[#DCC9D2]" />
                )}
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* LA MÉTHODE EN VIDÉO */}
      <section className="px-6 md:px-10 py-24 md:py-32">
        <div className="max-w-7xl mx-auto text-center">
          <div className="text-[11px] tracking-[0.3em] uppercase text-[#C0923F] mb-6">
            {adultes.videoMethode.eyebrow}
          </div>
          <h2 className="font-serif-display text-[28px] md:text-[44px] leading-[1.1] text-[#1F1B1D] mb-5">
            {adultes.videoMethode.title}
          </h2>
          <p className="font-serif-display italic text-[18px] md:text-[20px] text-[#3A3236] max-w-xl mx-auto mb-12 md:mb-14">
            {adultes.videoMethode.subtitle}
          </p>

          <div className="mx-auto w-full" style={{ maxWidth: '400px' }}>
            <div className="relative w-full overflow-hidden bg-[#1F1B1D]" style={{ aspectRatio: '9 / 16' }}>
              <iframe
                src={`https://www.youtube.com/embed/${adultes.videoMethode.youtubeId}?rel=0&modestbranding=1`}
                title={adultes.videoMethode.title}
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="absolute inset-0 w-full h-full"
                frameBorder={0}
              />
            </div>
          </div>
        </div>
      </section>

      {/* CE QUE ÇA CHANGE CONCRÈTEMENT */}
      <section className="px-6 md:px-10 py-24 md:py-32">
        <div className="max-w-7xl mx-auto grid md:grid-cols-12 gap-12 md:gap-16 items-start">
          <div className="md:col-span-5">
            <img src={images.paintDetail} alt="Détail de peinture — texture et matière issues du parcours Crée le canevas de ta vie" loading="lazy" className="w-full aspect-[3/4] object-cover" />
          </div>
          <div className="md:col-span-7">
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#C0923F] mb-6">{adultes.transformations.eyebrow}</div>
            <h2 className="font-serif-display text-[28px] md:text-[44px] leading-[1.1] text-[#1F1B1D] mb-6">
              {adultes.transformations.title}
            </h2>
            <p className="font-serif-display italic text-[18px] md:text-[20px] leading-[1.55] text-[#3A3236] mb-10 max-w-xl">
              {adultes.transformations.intro}
            </p>
            <div className="space-y-6">
              {adultes.transformations.items.map(it => (
                <div key={it.t} className="flex gap-5 items-start">
                  <Check size={18} className="text-[#9E3A5A] mt-2 shrink-0" />
                  <div>
                    <div className="font-serif-display text-2xl text-[#1F1B1D]">{it.t}</div>
                    <p className="text-[15px] leading-[1.7] text-[#3A3236] mt-1">{it.b}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10 pl-6 border-l-2 border-[#9E3A5A] max-w-xl">
              <p className="font-serif-display italic text-xl md:text-2xl text-[#3A3236] leading-[1.5]">
                {adultes.transformations.quote}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* TÉMOIGNAGES */}
      <section className="bg-[#F6EFF4] border-y border-[#DCC9D2] px-6 md:px-10 py-24 md:py-32">
        <div className="max-w-7xl mx-auto">
          <div className="text-[11px] tracking-[0.3em] uppercase text-[#C0923F] mb-8">{adultes.temoignages.eyebrow}</div>
          <h2 className="font-serif-display text-[28px] md:text-[44px] leading-[1.1] mb-16 md:mb-20 text-[#2C2C2C]">
            {adultes.temoignages.title}
          </h2>

          <div className="grid md:grid-cols-3 gap-px bg-[#DCC9D2]">
            {adultes.temoignages.items.map((t, i) => (
              <div key={i} className="bg-[#F6EFF4] flex flex-col">
                <blockquote className="p-10 md:p-12 flex flex-col flex-1">
                  <div className="font-serif-display italic text-5xl text-[#9E3A5A] mb-6 leading-none">“</div>
                  <p className="font-serif-display text-[17px] md:text-[18px] leading-[1.6] text-[#2C2C2C] mb-8 flex-1">
                    {t.q}
                  </p>
                  <footer className="text-[11px] tracking-[0.25em] uppercase text-[#C0923F]">
                    — {t.n}{t.context ? `, ${t.context}` : ''}
                  </footer>
                </blockquote>
                {'images' in t && Array.isArray((t as any).images) && (
                  <div className="px-10 md:px-12 pb-10 md:pb-12 grid grid-cols-2 gap-3">
                    <img
                      src={(t as any).images[0]}
                      alt={`Portrait de ${t.n}`}
                      className="w-full aspect-[3/4] object-cover"
                      loading="lazy"
                    />
                    <div className="flex flex-col gap-2">
                      <img
                        src={(t as any).images[1]}
                        alt={`Peinture réalisée par ${t.n}`}
                        className="w-full aspect-[3/4] object-cover"
                        loading="lazy"
                      />

                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <p className="mt-10 text-[12px] text-[#3A3236]/70 italic">
            {adultes.temoignages.note}
          </p>
        </div>
      </section>

      {/* OFFRE */}
      <section className="px-6 md:px-10 py-24 md:py-32">
        <div className="max-w-7xl mx-auto grid md:grid-cols-12 gap-12 md:gap-16 items-end">
          <div className="md:col-span-7">
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#C0923F] mb-8">{adultes.offre.eyebrow}</div>
            <h2 className="font-serif-display text-[28px] md:text-[44px] leading-[1.1] text-[#1F1B1D] mb-8 whitespace-pre-line">
              {adultes.offre.title}
            </h2>
            <p className="font-serif-display italic text-[18px] md:text-[20px] leading-[1.55] text-[#3A3236] max-w-xl mb-8">
              {adultes.offre.tagline}
            </p>
            <p className="text-[16px] leading-[1.75] text-[#3A3236] max-w-xl">
              {adultes.offre.body}
            </p>
          </div>
          <div className="md:col-span-5">
            <div className="bg-[#EFE5EC] border border-[#DCC9D2] p-10">
              <div className="font-serif-display italic text-[#9E3A5A] text-2xl mb-6">{adultes.offre.cardLabel}</div>
              <ul className="divide-y divide-[#DCC9D2]">
                {adultes.offre.details.map(([k, v]) => (
                  <li key={k} className="flex justify-between items-baseline py-4 gap-4">
                    <span className="text-[11px] tracking-[0.25em] uppercase text-[#C0923F] shrink-0">{k}</span>
                    <span className="font-serif-display text-xl md:text-2xl text-[#1F1B1D] text-right">{v}</span>
                  </li>
                ))}
              </ul>
              <a
                href={links.booking}
                target="_blank"
                rel="noreferrer"
                className="group mt-8 flex items-center justify-center gap-3 text-center px-6 py-4 bg-[#9E3A5A] text-[#F6EFF4] text-[12px] tracking-[0.25em] uppercase hover:bg-[#7A2D46] transition-colors duration-500"
              >
                {adultes.offre.ctaLabel}
                <ArrowRight size={14} className="transition-transform duration-500 group-hover:translate-x-1" />
              </a>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto mt-20">
          <img src={images.atelierRoom} alt="Atelier HeartCanvas en Suisse — espace d'accompagnement par l'art pour adultes" loading="lazy" className="w-full aspect-[16/7] object-cover" />
        </div>
      </section>

      {/* AUTRE PROPOSITION — TRAVERSER LE DEUIL PAR L'ART */}
      <section className="bg-[#F6EFF4] border-y border-[#DCC9D2] px-6 md:px-10 py-24 md:py-32">
        <div className="max-w-7xl mx-auto grid md:grid-cols-12 gap-12 md:gap-16 items-start">
          <div className="md:col-span-5">
            <img src={images.deuil} alt="Traverser le deuil par l'art — atelier d'expression et de transformation HeartCanvas" loading="lazy" className="w-full aspect-[3/4] object-cover" />
          </div>
          <div className="md:col-span-7">
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#C0923F] mb-6">{adultes.deuilOffre.eyebrow}</div>
            <div className="text-[12px] tracking-[0.25em] uppercase text-[#3A3236]/70 mb-4">{adultes.deuilOffre.audience}</div>
            <h2 className="font-serif-display text-[28px] md:text-[44px] leading-[1.1] text-[#2C2C2C] mb-10">
              {adultes.deuilOffre.title}
            </h2>
            <div className="space-y-5 max-w-xl">
              {adultes.deuilOffre.paragraphs.map((p, i) => (
                <p key={i} className="text-[16px] md:text-[17px] leading-[1.75] text-[#2C2C2C]">{p}</p>
              ))}
            </div>
            <div className="mt-10 pl-6 border-l-2 border-[#9E3A5A] max-w-xl">
              <p className="font-serif-display italic text-lg md:text-xl text-[#2C2C2C] leading-[1.5]">
                {adultes.deuilOffre.footnote}
              </p>
            </div>
            <Link
              to="/contact"
              className="group mt-10 inline-flex items-center gap-3 px-8 py-4 bg-[#9E3A5A] text-[#F6EFF4] text-[12px] tracking-[0.25em] uppercase hover:bg-[#7A2D46] transition-colors duration-500"
            >
              {adultes.deuilOffre.ctaLabel}
              <ArrowRight size={14} className="transition-transform duration-500 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* INVITATION FINALE */}
      <PageCTA
        eyebrow={adultes.invitationFinale.eyebrow}
        title={adultes.invitationFinale.title}
        body={adultes.invitationFinale.body}
        ctaLabel={adultes.invitationFinale.ctaLabel}
        ctaHref={links.booking}
      />
    </div>
  )
}

export default Adultes
